import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, AlertTriangle, BarChart3, Calendar, CheckCircle2, ClipboardCheck,
  HeartHandshake, RefreshCw, Settings, ShoppingBag,
  Ticket, UserCheck, Users,
} from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const ACTIONS = [
  { to: "/app/asignaciones", permission: "assignments.manage", title: "Asignaciones", desc: "Asignar responsables y acompañamientos.", icon: UserCheck },
  { to: "/app/revision-diagnosticos", permission: "diagnostics.review", title: "Revisión diagnóstica", desc: "Revisar expedientes en flujo operativo.", icon: ClipboardCheck },
  { to: "/app/solicitudes-roles", permission: "consultant.requests.review", title: "Solicitudes de rol", desc: "Revisar solicitudes; decisiones reservadas respetan el rol Director.", icon: Users },
  { to: "/app/usuarios", permission: "users.view", title: "Consulta de usuarios", desc: "Buscar y filtrar cuentas sin acciones críticas.", icon: Users },
  { to: "/app/actividad-usuarios", permission: "activity.overview", title: "Actividad de usuarios", desc: "Ver sesiones, tiempo activo y últimos accesos.", icon: Activity },
  { to: "/app/radar-retencion", permission: "activity.overview", title: "Radar de retención", desc: "Identificar activación, recurrencia y señales tempranas de abandono.", icon: BarChart3 },
  { to: "/app/acompanamientos-admin", permission: "assignments.manage", title: "Acompañamientos", desc: "Coordinar y dar seguimiento operativo.", icon: HeartHandshake },
  { to: "/app/agenda", permission: "agenda.manage", title: "Agenda global", desc: "Organizar citas y disponibilidad del servicio.", icon: Calendar },
  { to: "/app/marketplace-admin", permission: "marketplace.products.review", title: "Marketplace", desc: "Revisar productos y pedidos reportados.", icon: ShoppingBag },
  { to: "/app/tickets", permission: "tickets.view", title: "Soporte y tickets", desc: "Responder, escalar y cerrar incidencias.", icon: Ticket },
  { to: "/app/reportes", permission: "system.config.view", title: "Reportes operativos", desc: "Ver indicadores reales del servicio.", icon: BarChart3 },
  { to: "/app/configuracion-operativa", permission: "system.config.view", title: "Parámetros vigentes", desc: "Consulta de configuración en modo solo lectura.", icon: Settings },
];

const METRICS = [
  { key: "open_tickets", label: "Tickets abiertos", icon: Ticket },
  { key: "diagnostics_in_flow", label: "Diagnósticos en flujo", icon: ClipboardCheck },
  { key: "accompaniments_in_flow", label: "Acompañamientos activos", icon: HeartHandshake },
];

const AdminDashboard = () => {
  const { user, accessMap } = useAuth();
  const permissions = useMemo(() => new Set(accessMap?.permissions || []), [accessMap]);
  const actions = useMemo(() => ACTIONS.filter((item) => permissions.has(item.permission)), [permissions]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOperations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/admin/reports/operations", { params: { days: 30 } });
      setTotals(data?.totals || {});
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOperations(); }, [loadOperations]);

  return (
    <PageShell
      kicker="Administrador KD9 · Operación"
      title="CENTRO ADMINISTRATIVO"
      desc={`Bienvenido, ${user?.name || "Administrador"}. Gestioná la operación diaria desde datos reales y permisos verificados.`}
      testId="admin-dashboard"
      badge={
        <span className="inline-flex items-center gap-2 text-[10px] font-mont uppercase tracking-widest" style={{ color: "#86EFAC" }}>
          <CheckCircle2 size={14} /> Servicio operativo
        </span>
      }
    >
      <div
        className="mb-6 rounded-2xl border p-4 flex items-start gap-3"
        style={{ borderColor: "rgba(212,175,55,.35)", background: "rgba(212,175,55,.06)" }}
        data-testid="admin-scope"
      >
        <AlertTriangle size={18} className="kd9-gold-text shrink-0 mt-0.5" />
        <div>
          <div className="font-mont text-xs font-semibold uppercase tracking-widest">Alcance administrativo</div>
          <p className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>
            Podés revisar, coordinar, responder y escalar. Aprobar pagos, activar Premium,
            cambiar roles, eliminar usuarios y editar la configuración global siguen reservados al Director KD9.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-5 rounded-xl border p-4 flex items-center justify-between gap-3" style={{ color: "#FCA5A5", borderColor: "rgba(122,14,23,.4)" }}>
          <span className="font-mont text-sm">{error}</span>
          <button onClick={loadOperations} className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2">
            <RefreshCw size={12} /> Reintentar
          </button>
        </div>
      )}

      <section className="mb-8" aria-labelledby="admin-priority-title">
        <h2 id="admin-priority-title" className="font-cinzel tracking-[.18em] text-sm mb-4 kd9-gold-text">ESTADO OPERATIVO</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {METRICS.map(({ key, label, icon: Icon }) => (
            <div key={key} className="kd9-card p-4" data-testid={`admin-metric-${key}`}>
              <Icon size={17} className="kd9-gold-text mb-3" />
              <div className="font-cinzel text-2xl">{loading ? "…" : Number(totals[key] || 0).toLocaleString("es")}</div>
              <div className="font-mont text-[10px] uppercase tracking-widest mt-1" style={{ color: "var(--kd9-text-soft)" }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="admin-actions-title">
        <h2 id="admin-actions-title" className="font-cinzel tracking-[.18em] text-sm mb-4 kd9-gold-text">OPERACIÓN AUTORIZADA</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {actions.map(({ to, title, desc, icon: Icon }) => (
            <Link key={to} to={to} className="kd9-card p-5 group transition-transform hover:-translate-y-0.5" data-testid={`admin-action-${title.toLowerCase().replaceAll(" ", "-")}`}>
              <div className="flex items-start gap-3">
                <div className="rounded-xl p-2.5 shrink-0" style={{ background: "rgba(60,120,181,.12)", color: "#7BB8E8" }}>
                  <Icon size={18} />
                </div>
                <div>
                  <div className="font-mont font-semibold text-sm">{title}</div>
                  <p className="font-cormorant text-base mt-1" style={{ color: "var(--kd9-text-soft)" }}>{desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
};

export default AdminDashboard;
